import { useTranslation } from "react-i18next";
import { ExternalLink, Play } from "lucide-react";
import { cn, FACEBOOK_FEED_ASPECT_CLASS, MEDIA_BACKDROP_HOST_CLASS, MediaBackdrop, MediaPreview } from "@nexus/ui";
import type { AskNexusCreativeExample, AskNexusCreativeExamplesCard, AskNexusOpportunitiesCard } from "@/features/ask-nexus/api/ask-nexus.api";
import { CardFrame } from "@/features/ask-nexus/components/AskNexusCards";
import { AskNexusNexusLink } from "@/features/ask-nexus/components/AskNexusNexusLink";
import { exampleBrand, exampleMetrics, opportunityDetails } from "@/features/ask-nexus/lib/card-format";
import { opportunityLink } from "@/features/ask-nexus/lib/nexus-links";

/** Ads the tool returned, as the prototype's ad cards: own ads with their performance, competitors' with activity only. */
export function AskNexusCreativeExamples({ card }: { card: AskNexusCreativeExamplesCard }) {
  return (
    <CardFrame card={card} testId="ask-nexus-card-ads">
      <div className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2">
        {card.examples.map((example) => (
          <AdCard key={example.hash} example={example} />
        ))}
      </div>
    </CardFrame>
  );
}

/** Opportunities saved in Nexus, stacked, each with the rank Nexus gave it within its set. */
export function AskNexusOpportunities({ card }: { card: AskNexusOpportunitiesCard }) {
  const { t, i18n } = useTranslation();
  return (
    <CardFrame card={card} testId="ask-nexus-card-opportunities">
      <ul className="flex flex-col gap-2">
        {card.items.map((item) => (
          <li key={item.id} className="rounded-xl bg-(--an-inset) px-3.5 py-3">
            <div className="flex flex-wrap gap-1">
              <Chip>{t("askNexus.cards.opportunity.inNexus", "In Nexus · {{set}}", { set: item.setName })}</Chip>
              <Chip>{t("askNexus.cards.opportunity.rankChip", "Rank {{rank}} in its set", { rank: item.rank })}</Chip>
            </div>
            <p className="mt-2 text-[13px] leading-snug font-medium">{item.angle}</p>
            <p className="mt-0.5 text-xs text-(--an-ink-2)">
              {t("askNexus.cards.opportunity.persona", "Persona: {{text}}", { text: item.personaTitle })} · {item.type}
            </p>
            <ul className="mt-2 space-y-0.5 text-xs text-(--an-ink-2)">
              {opportunityDetails(t, item, i18n.language).map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <AskNexusNexusLink link={opportunityLink(item)} className="mt-2.5" />
          </li>
        ))}
      </ul>
    </CardFrame>
  );
}

function AdCard({ example }: { example: AskNexusCreativeExample }) {
  const { t, i18n } = useTranslation();
  const metrics = exampleMetrics(t, example, i18n.language);
  const [lead, ...rest] = metrics;
  const brand = exampleBrand(t, example);

  return (
    <div className="flex flex-[0_0_176px] snap-start flex-col overflow-hidden rounded-2xl border border-(--an-hair) bg-(--an-field)" data-testid="ask-nexus-ad">
      <AdMedia example={example} meta={[brand, ...(example.platform ? [example.platform] : [])].join(" · ")} />
      {/* The cards in a row share one height; the link sits at the bottom of each, in one line across the row. */}
      <div className="flex flex-1 flex-col p-3">
        <div className="flex flex-wrap gap-1">
          <Chip isStrong>{brand}</Chip>
          {example.platform ? <Chip>{example.platform}</Chip> : null}
          {example.hook ? <Chip tone="info">{example.hook}</Chip> : null}
        </div>
        <p className="mt-2 line-clamp-2 text-xs leading-snug font-medium">{example.adName}</p>
        {example.adCopy ? <p className="mt-1 line-clamp-3 text-[11px] leading-snug text-(--an-ink-2)">{example.adCopy}</p> : null}
        {example.runDates ? <p className="mt-1 text-[10px] text-(--an-ink-3) tabular-nums">{example.runDates}</p> : null}
        {lead ? (
          <dl className="mt-2.5 flex flex-col gap-1">
            <div className="flex justify-between text-xs">
              <dt className="font-medium">{lead.label}</dt>
              <dd className="text-base font-medium tracking-[-0.02em] tabular-nums">{lead.value}</dd>
            </div>
            {rest.map((metric) => (
              <div key={metric.label} className="flex justify-between text-xs text-(--an-ink-2)">
                <dt>{metric.label}</dt>
                <dd className="font-medium text-(--an-ink) tabular-nums">{metric.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {example.library ? (
          <p className="mt-2.5 text-[11px] leading-snug text-(--an-ink-3)">
            {t("askNexus.cards.example.competitorNote", "Competitor ad: Nexus has its activity, not its spend or results.")}
          </p>
        ) : null}
        {example.creativeUrl ? (
          <a
            href={example.creativeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex min-h-8 items-center gap-1 self-start pt-2.5 text-[11px] text-(--an-ink-2) underline-offset-2 hover:text-(--an-ink) hover:underline"
          >
            {t("askNexus.cards.example.openOriginal", "Open original")}
            <ExternalLink className="size-3" aria-hidden="true" />
          </a>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The ad's image or video through Nexus's MediaPreview, from the URLs the tool
 * returned: the image keeps its own aspect ratio over a blurred backdrop, and a
 * video opens with controls and waits for the viewer to press play.
 */
function AdMedia({ example, meta }: { example: AskNexusCreativeExample; meta: string }) {
  const { t } = useTranslation();
  const isVideo = example.mediaType === "video";
  const preview = example.thumbnailUrl ?? (isVideo ? null : example.creativeUrl);
  const frame = `${FACEBOOK_FEED_ASPECT_CLASS} ${MEDIA_BACKDROP_HOST_CLASS} ask-nexus-hatch relative`;

  if (!preview && !example.creativeUrl) {
    return (
      <div className={frame}>
        <span className="absolute inset-x-2 bottom-2 text-[10px] text-(--an-ink-3)">{t("askNexus.cards.example.noImage", "No image returned")}</span>
        <RankBadge rank={example.rank} />
      </div>
    );
  }
  return (
    <MediaPreview
      src={example.creativeUrl ?? undefined}
      thumbnailSrc={preview ?? ""}
      type={example.mediaType}
      alt={example.adName}
      title={example.adName}
      meta={<span>{meta}</span>}
      hoverPlayback={false}
      autoPlay={false}
      className={frame}
    >
      {preview ? <MediaBackdrop src={preview} /> : (
        <span className="absolute inset-x-2 bottom-2 text-[10px] text-(--an-ink-3)">{t("askNexus.cards.example.noPoster", "No preview image returned")}</span>
      )}
      <RankBadge rank={example.rank} />
      {isVideo ? (
        <span className="pointer-events-none absolute top-1/2 left-1/2 grid size-9 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-[#1a1a1a]">
          <Play className="ml-0.5 size-3.5 fill-current stroke-none" aria-hidden="true" />
        </span>
      ) : null}
    </MediaPreview>
  );
}

function RankBadge({ rank }: { rank: number | null }) {
  if (rank === null) return null;
  return (
    <span className="absolute top-2 left-2 z-10 inline-flex h-[22px] items-center rounded-full bg-white/90 px-2 text-[10px] font-medium text-[#1a1a1a]">
      #{rank}
    </span>
  );
}

function Chip({ children, isStrong = false, tone = "neutral" }: { children: string; isStrong?: boolean; tone?: "neutral" | "info" }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 max-w-full items-center truncate rounded-full px-2 text-[10px]",
        tone === "info" ? "bg-(--an-info-tint) text-(--an-info-ink)" : "bg-(--an-neutral-tint) text-(--an-ink-2)",
        isStrong && "font-medium text-(--an-ink)",
      )}
    >
      {children}
    </span>
  );
}
